import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, originUrl, isAllowedImage, fmtTime12, fmtDateLong } from "@/lib/api";
import SessionDatePicker from "@/components/SessionDatePicker";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Upload, Calendar, Users, Palette, Sparkles, FileText, Shield, CreditCard, Image as ImageIcon } from "lucide-react";

const RUG_CATEGORIES = new Set(["rug_tufting", "tuft_sip", "seasonal", "youth", "special"]);

export default function BookWorkshop() {
  const { id } = useParams();
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [promo, setPromo] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [form, setForm] = useState({
    rug_size_id: "", guest_sizes: [], addon_ids: [], guests: 1,
    theme_design_id: "", participant_designs: [], design_images: [], active_participant: 0, promo_code: "", tip_choice: 0, tip_custom: "",
    design_notes: "", design_image_data: null, design_image_name: null,
    name: "", email: "", phone: "", notes: "",
    policies_accepted: [], tuft_sip_acknowledged: false,
  });

  useEffect(() => {
    api.get(`/workshops/${id}`).then((r) => setW(r.data));
    api.get("/booking-config").then((r) => setCfg(r.data));
  }, [id]);

  useEffect(() => {
    if (w?.available_sessions?.length === 1 && !id.includes("::")) nav(`/workshops/${w.id}::${w.available_sessions[0].id}`, { replace: true });
  }, [w, id, nav]);

  const category = (w?.category || "").toLowerCase();
  const isRugFlow = !!w && (w.is_theme || RUG_CATEGORIES.has(category));
  const sipAllowed = !!w && category !== "tuft_buddies" && w.allow_addons !== false;

  const availableDesigns = useMemo(() => (w?.theme_designs || []).filter((d) => !d.hidden && !d.unavailable), [w]);

  const hasDesignPicker = availableDesigns.length > 0;

  const STEPS = useMemo(() => {
    const s = [];
    s.push({ id: "guests", label: "Guests", icon: Users });
    if (isRugFlow) s.push({ id: "size", label: "Rug Sizes", icon: Sparkles });
    if (sipAllowed) s.push({ id: "addons", label: "Sip", icon: Palette });
    if (hasDesignPicker) s.push({ id: "design_pick", label: "Pick Design", icon: ImageIcon });
    if (isRugFlow || !hasDesignPicker) s.push({ id: "design", label: "Design", icon: FileText });
    s.push({ id: "info", label: "Your Info", icon: Users });
    s.push({ id: "review", label: "Review", icon: Check });
    s.push({ id: "policies", label: "Policies & Pay", icon: Shield });
    return s;
  }, [isRugFlow, sipAllowed, hasDesignPicker]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [step]);
  useEffect(() => {
    setForm((f) => { const n = Number(f.guests) || 0; const gs = f.guest_sizes.slice(0, n); while (gs.length < n) gs.push(""); return { ...f, guest_sizes: gs, rug_size_id: gs[0] || "" }; });
    setPromo(null);
  }, [form.guests]);
  useEffect(() => { setPromo(null); }, [form.guest_sizes.join(",")]);

  const sizes = useMemo(() => {
    if (!cfg || !w || !isRugFlow) return [];
    return (w.allowed_size_ids && w.allowed_size_ids.length) ? cfg.rug_sizes.filter((s) => w.allowed_size_ids.includes(s.id)) : cfg.rug_sizes;
  }, [cfg, w, isRugFlow]);
  const addons = useMemo(() => (cfg && sipAllowed) ? cfg.addons : [], [cfg, sipAllowed]);
  const policies = (cfg?.policies || []).filter((p) => p.id !== "booking");
  const tuftSipSelected = form.addon_ids.includes("tuft_sip");
  const tuftSipDisclaimer = cfg?.tuft_sip_disclaimer || "";
  const designInstructions = w?.design_instructions || cfg?.design_instructions || "";

  const breakdown = useMemo(() => {
    if (!cfg || !w) return { rug_subtotal: 0, addons_subtotal: 0, subtotal: 0, discount: 0, tip: 0, tax: 0, tax_rate: 0, total: 0, chosenAddons: [], guests: 0, size: null, base_ppg: 0 };
    const size = sizes.find((s) => s.id === form.rug_size_id);
    const chosen = addons.filter((a) => form.addon_ids.includes(a.id));
    const guests = Number(form.guests) || 0;
    const priceOf = (sz) => { if (!sz) return 0; const sale = sz.sale_active && sz.sale_price_per_person != null && Number(sz.sale_price_per_person) < Number(sz.price_per_person); return sale ? Number(sz.sale_price_per_person) : Number(sz.price_per_person || 0); };
    let base_ppg = 0; let rug_subtotal = 0; let guestSizes = [];
    if (isRugFlow) {
      guestSizes = form.guest_sizes.slice(0, guests).map((sid) => sizes.find((s) => s.id === sid)).filter(Boolean);
      rug_subtotal = guestSizes.reduce((s, sz) => s + priceOf(sz), 0);
      base_ppg = guestSizes.length ? rug_subtotal / guestSizes.length : 0;
    } else {
      const saleActive = w.sale_active && w.sale_price != null && Number(w.sale_price) < Number(w.price);
      base_ppg = saleActive ? Number(w.sale_price) : Number(w.price || 0);
      rug_subtotal = base_ppg * guests;
    }
    const addons_subtotal = chosen.reduce((s, a) => s + a.price_per_person, 0) * guests;
    const subtotal = rug_subtotal + addons_subtotal;
    const discount = promo && promo.discount ? Math.min(Number(promo.discount), subtotal) : 0;
    const afterDiscount = subtotal - discount;
    const tx = cfg.sales_tax || {};
    const rate = tx.enabled ? Number(tx.rate || 0) : 0;
    let taxable = ((tx.workshops_taxable !== false && w.taxable !== false) ? rug_subtotal : 0) + (tx.addons_taxable !== false ? addons_subtotal : 0);
    if (discount && subtotal) taxable = taxable * (afterDiscount / subtotal);
    const tax = rate ? +(taxable * rate / 100).toFixed(2) : 0;
    const tip = form.tip_choice === "custom" ? Math.max(0, Number(form.tip_custom) || 0) : +(afterDiscount * (Number(form.tip_choice) || 0) / 100).toFixed(2);
    return {
      size, guestSizes, chosenAddons: chosen, guests, base_ppg, tip: +tip.toFixed(2),
      rug_subtotal: +rug_subtotal.toFixed(2),
      addons_subtotal: +addons_subtotal.toFixed(2),
      subtotal: +subtotal.toFixed(2), discount: +discount.toFixed(2), tax_rate: rate, tax,
      total: +(afterDiscount + tax + tip).toFixed(2),
    };
  }, [cfg, sizes, addons, form, isRugFlow, w, promo]); // eslint-disable-line

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    try {
      const { data } = await api.post("/promo/validate", { code, workshop_id: id, subtotal: breakdown.subtotal, guests: breakdown.guests, base_ppg: breakdown.base_ppg, email: form.email, rug_size_id: isRugFlow ? form.rug_size_id : "", guest_sizes: isRugFlow ? form.guest_sizes.slice(0, Number(form.guests)) : [] });
      setPromo(data); setForm((f) => ({ ...f, promo_code: data.code })); toast.success(`Promo applied: -$${Number(data.discount).toFixed(2)}`);
    } catch (e) { setPromo(null); setForm((f) => ({ ...f, promo_code: "" })); toast.error(e.response?.data?.detail || "Invalid promo code"); }
  };

  const spots = w ? Math.max(0, (w.capacity || 0) - (w.booked || 0)) : 0;
  const requiredPolicyIds = policies.filter((p) => p.required).map((p) => p.id);
  const missing = requiredPolicyIds.filter((id) => !form.policies_accepted.includes(id));

  const selectedDesign = availableDesigns.find((d) => d.id === form.theme_design_id);
  const guestsN = Number(form.guests) || 0;
  const designsComplete = form.participant_designs.length >= guestsN && form.participant_designs.slice(0, guestsN).every((p) => p?.design_id);
  const setParticipantDesign = (i, design_id) => setForm((f) => {
    const pd = [...f.participant_designs]; pd[i] = { design_id };
    const nextI = Array.from({ length: guestsN }, (_, k) => k).find((k) => !pd[k]?.design_id);
    return { ...f, participant_designs: pd, theme_design_id: pd[0]?.design_id || design_id, active_participant: nextI ?? i };
  });
  const assignImage = (i, image_index) => setForm((f) => { const pd = [...f.participant_designs]; pd[i] = { image_index }; return { ...f, participant_designs: pd }; });

  const canNext = () => {
    switch (STEPS[step].id) {
      case "size": return form.guest_sizes.slice(0, Number(form.guests)).every(Boolean) && Number(form.guests) >= 1;
      case "addons": return !tuftSipSelected || form.tuft_sip_acknowledged;
      case "guests": return form.guests >= 1 && form.guests <= spots;
      case "design_pick": return designsComplete;
      case "info": return form.name && form.email;
      case "policies": return missing.length === 0 && (!tuftSipSelected || form.tuft_sip_acknowledged);
      default: return true;
    }
  };

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));
  const toggleAddon = (aid) => setForm((f) => ({ ...f, addon_ids: f.addon_ids.includes(aid) ? f.addon_ids.filter((x) => x !== aid) : [...f.addon_ids, aid] }));
  const allAccepted = policies.length > 0 && policies.every((p) => form.policies_accepted.includes(p.id));
  const toggleAll = (on) => setForm((f) => ({ ...f, policies_accepted: on ? policies.map((p) => p.id) : [] }));

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    if (!isAllowedImage(file)) return toast.error("Please upload a HEIC, JPG or PNG image");
    if (file.size > 4_000_000) return toast.error("File too large (max 4MB)");
    if (form.design_images.length >= guestsN) return toast.error(`You can upload up to ${guestsN} image${guestsN === 1 ? "" : "s"} (one per participant)`);
    const data = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    setForm((f) => {
      const imgs = [...f.design_images, { name: file.name, data }];
      const idx = imgs.length - 1;
      const pd = [...f.participant_designs];
      const firstEmpty = Array.from({ length: guestsN }, (_, k) => k).find((k) => pd[k]?.image_index == null);
      if (firstEmpty != null) pd[firstEmpty] = { image_index: idx };
      return { ...f, design_images: imgs, participant_designs: pd, design_image_data: imgs[0].data, design_image_name: imgs[0].name };
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        workshop_id: id, origin_url: originUrl(),
        ...form, tip: breakdown.tip, guests: Number(form.guests), guest_sizes: isRugFlow ? form.guest_sizes.slice(0, Number(form.guests)) : [], participant_designs: form.participant_designs.slice(0, Number(form.guests)), active_participant: undefined,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      setDone(data);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking");
      setBusy(false);
    }
  };

  if (done) return (
    <div className="max-w-2xl mx-auto px-6 lg:px-8 py-16" data-testid="booking-pending">
      <div className="bg-white rounded-3xl p-8 shadow-card text-center">
        <div className="text-terracotta text-sm tracking-widest uppercase font-bold">Booking received</div>
        <h1 className="mt-2 font-serif text-4xl">Almost there!</h1>
        <p className="mt-4 text-charcoal/75">We've reserved your spot for <b>{w?.name}</b>. Your booking is <b>pending payment</b> — complete your payment with Square and we'll confirm by email.</p>
        <div className="mt-6 rounded-2xl bg-cream-alt p-4 text-left text-sm">
          <div className="flex justify-between"><span>Date</span><b>{fmtDateLong(w?.date, { month: 'short', day: 'numeric' })} · {fmtTime12(w?.start_time)}</b></div>
          <div className="flex justify-between mt-1"><span>Guests</span><b>{form.guests}</b></div>
          <div className="flex justify-between mt-1"><span>Total due</span><b data-testid="booking-pending-total">${Number(done.amount).toFixed(2)}</b></div>
        </div>
        {done.square_link ? (
          <a href={done.square_link} target="_blank" rel="noreferrer" data-testid="pay-with-square-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal text-cream px-8 py-4 font-bold shadow-button"><CreditCard size={18}/> Pay with Square</a>
        ) : (
          <p className="mt-6 text-sm text-charcoal-muted" data-testid="pay-link-pending">We'll email you a secure Square payment link shortly.</p>
        )}
        <p className="mt-4 text-xs text-charcoal-muted">A copy of these details has been sent to {form.email}.</p>
      </div>
    </div>
  );

  if (!w || !cfg) return <div className="max-w-4xl mx-auto px-6 py-16 text-center text-charcoal-muted">Loading…</div>;
  if (w.available_sessions?.length > 1 && !id.includes("::")) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-10" data-testid="session-picker">
        <button onClick={() => nav(w.is_theme ? "/themes" : "/workshops")} className="text-terracotta font-semibold text-sm inline-flex items-center gap-1 mb-4" data-testid="back-to-list"><ChevronLeft size={16}/> Back to {w.is_theme ? "themes" : "workshops"}</button>
        <h1 className="font-serif text-3xl md:text-4xl">{w.name}</h1>
        <p className="mt-2 text-charcoal/70">Choose a date and time to continue.</p>
        <div className="mt-6">
          <SessionDatePicker sessions={w.available_sessions} onPick={(s) => nav(`/workshops/${w.id}::${s.id}`)}/>
        </div>
      </div>
    );
  }
  if (spots < 1) return <div className="max-w-3xl mx-auto px-6 py-16 text-center"><h1 className="font-serif text-4xl">Sold out</h1><button onClick={() => nav(w.is_theme ? "/themes" : "/workshops")} className="mt-6 rounded-full bg-terracotta text-white px-6 py-3 font-bold">Back</button></div>;

  const currentStepId = STEPS[step]?.id;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
      <button onClick={() => nav(w.is_theme ? "/themes" : "/workshops")} className="text-terracotta font-semibold text-sm inline-flex items-center gap-1 mb-4" data-testid="back-to-list"><ChevronLeft size={16}/> Back to {w.is_theme ? "themes" : "workshops"}</button>
      <div className="bg-charcoal text-cream rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="session-header">
        {w.photo_url && <img src={w.photo_url} alt={w.name} className="w-full md:w-48 h-32 md:h-32 rounded-2xl object-cover border-2 border-white/10"/>}
        <div className="flex-1">
          {w.is_theme && <div className="text-ochre-light text-xs tracking-widest uppercase font-bold mb-1">Theme</div>}
          <h1 className="font-serif text-3xl md:text-4xl leading-none text-cream">{w.name}</h1>
          <div className="mt-3 flex flex-wrap gap-3 text-sm text-cream/80">
            <span className="inline-flex items-center gap-1.5"><Calendar size={14}/> {fmtDateLong(w.date, { weekday: 'long', month: 'short', day: 'numeric' })} · {fmtTime12(w.start_time)}</span>
            <span className="inline-flex items-center gap-1.5"><Users size={14}/> {spots > 0 ? "Spots available" : "Sold Out"}</span>
            <span className="inline-flex items-center gap-1.5">Duration: {w.duration_minutes} min · Ages {w.age_requirement}</span>
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-2 overflow-x-auto pb-2" data-testid="wizard-progress">
        {STEPS.map((s, i) => (
          <div key={s.id} className={`flex items-center gap-2 shrink-0 ${i < step ? "text-forest" : i === step ? "text-terracotta" : "text-charcoal-muted"}`}>
            <div className={`w-8 h-8 rounded-full grid place-items-center border-2 ${i < step ? "bg-forest text-white border-forest" : i === step ? "bg-terracotta text-white border-terracotta" : "border-charcoal-muted/30"}`}>
              {i < step ? <Check size={16}/> : <s.icon size={14}/>}
            </div>
            <span className="text-xs font-bold hidden sm:inline">{s.label}</span>
            {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${i < step ? "bg-forest" : "bg-charcoal-muted/20"}`}></div>}
          </div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-3xl p-8 shadow-card min-h-[400px]" data-testid={`step-${currentStepId}`}>
          {currentStepId === "size" && (
            <div>
              <h2 className="font-serif text-3xl">Choose a rug size for each guest</h2>
              <p className="mt-2 text-charcoal/70">Every guest picks their own size — mix and match is fine.</p>
              <div className="mt-6 space-y-5" data-testid="guest-size-list">
                {Array.from({ length: Number(form.guests) || 0 }, (_, gi) => (
                  <div key={gi} className="rounded-3xl bg-white border-2 border-cream-alt p-4" data-testid={`guest-size-${gi}`}>
                    <div className="font-serif text-xl mb-3">Guest {gi + 1}{form.guest_sizes[gi] ? <span className="text-sm text-forest font-sans font-semibold ml-2">✓ {sizes.find((s) => s.id === form.guest_sizes[gi])?.label}</span> : <span className="text-sm text-terracotta font-sans font-semibold ml-2">choose a size</span>}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {sizes.map((s) => {
                        const saleActive = s.sale_active && s.sale_price_per_person != null && Number(s.sale_price_per_person) < Number(s.price_per_person);
                        const priceShown = saleActive ? s.sale_price_per_person : s.price_per_person;
                        const sel = form.guest_sizes[gi] === s.id;
                        return (
                          <button key={s.id} type="button" onClick={() => setForm((f) => { const gs = [...f.guest_sizes]; gs[gi] = s.id; return { ...f, guest_sizes: gs, rug_size_id: gs[0] || s.id }; })} data-testid={`size-${gi}-${s.id}`}
                            className={`text-left rounded-2xl p-4 border-2 transition-colors ${sel ? "border-terracotta bg-terracotta/5" : "border-cream-alt hover:border-terracotta/50"}`}>
                            <div className="font-serif text-lg leading-tight">{s.label}</div>
                            <div className="text-[11px] text-charcoal-muted">{s.dimensions}</div>
                            <div className="mt-2 font-serif text-2xl text-terracotta leading-none">${priceShown}</div>
                            {saleActive && <div className="text-[11px] text-charcoal-muted line-through">${s.price_per_person}</div>}
                          </button>);
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {currentStepId === "addons" && (
            <div>
              <h2 className="font-serif text-3xl">Add Sip?</h2>
              <p className="mt-2 text-charcoal/70">Optional BYOB add-on for guests 21+.</p>
              {addons.length === 0 && <p className="mt-6 text-charcoal-muted">No add-ons available for this session.</p>}
              <div className="mt-6 space-y-3">
                {addons.map((a) => (
                  <label key={a.id} className={`flex items-center justify-between rounded-2xl p-5 border-2 cursor-pointer ${form.addon_ids.includes(a.id) ? "border-terracotta bg-terracotta/5" : "border-cream-alt"}`}>
                    <div className="flex items-center gap-3">
                      <input data-testid={`addon-${a.id}`} type="checkbox" checked={form.addon_ids.includes(a.id)} onChange={() => toggleAddon(a.id)} className="w-5 h-5 accent-terracotta"/>
                      <div>
                        <div className="font-serif text-xl">{a.id === "tuft_sip" ? "Sip" : a.label}</div>
                        {a.description && <div className="text-sm text-charcoal-muted">{a.description}</div>}
                      </div>
                    </div>
                    <div className="font-serif text-terracotta text-xl">+${a.price_per_person}<span className="text-xs text-charcoal-muted"> /guest</span></div>
                  </label>
                ))}
              </div>
              {tuftSipSelected && (
                <div className="mt-6 rounded-2xl bg-ochre/10 border-2 border-ochre p-5" data-testid="sip-disclaimer-box">
                  <div className="text-xs text-charcoal-muted whitespace-pre-line">{tuftSipDisclaimer}</div>
                  <label className="mt-4 flex items-start gap-2 cursor-pointer text-sm">
                    <input data-testid="sip-ack" type="checkbox" checked={form.tuft_sip_acknowledged} onChange={(e) => setForm({...form, tuft_sip_acknowledged: e.target.checked})} className="mt-1 accent-terracotta"/>
                    <span className="font-semibold">I have read and agree to the Sip terms.</span>
                  </label>
                </div>
              )}
            </div>
          )}
          {currentStepId === "guests" && (
            <div>
              <h2 className="font-serif text-3xl">How many guests?</h2>
              <p className="mt-2 text-charcoal/70">This session has <b className="text-forest">{spots} spots</b> remaining.</p>
              <div className="mt-8 flex items-center justify-center gap-6">
                <button onClick={() => setForm({...form, guests: Math.max(1, Number(form.guests) - 1)})} className="w-14 h-14 rounded-full bg-cream-alt text-3xl font-bold" data-testid="guests-minus">−</button>
                <input data-testid="guests-input" type="number" min="1" max={spots} value={form.guests} onChange={(e) => setForm({...form, guests: Math.min(spots, Math.max(1, Number(e.target.value) || 1))})} className="w-32 rounded-2xl border-2 border-cream-alt text-center text-4xl font-serif py-3 focus:border-terracotta focus:outline-none"/>
                <button onClick={() => setForm({...form, guests: Math.min(spots, Number(form.guests) + 1)})} className="w-14 h-14 rounded-full bg-cream-alt text-3xl font-bold" data-testid="guests-plus">+</button>
              </div>
              {form.guests > spots && <div className="mt-4 text-red-600 text-center text-sm font-semibold">Not enough availability for that many guests — please choose fewer or another date.</div>}
            </div>
          )}
          {currentStepId === "design_pick" && (
            <div>
              <h2 className="font-serif text-3xl">Pick a design for each participant</h2>
              <p className="mt-2 text-charcoal/70">Choose one gallery design per rug. The same design can be picked more than once.</p>
              <div className="mt-5 flex flex-wrap gap-2" data-testid="participant-tabs">
                {Array.from({ length: guestsN }, (_, i) => {
                  const d = availableDesigns.find((x) => x.id === form.participant_designs[i]?.design_id);
                  const active = form.active_participant === i;
                  return (
                    <button key={i} type="button" onClick={() => setForm({ ...form, active_participant: i })} data-testid={`participant-tab-${i}`}
                      className={`rounded-full px-4 py-2 text-sm font-semibold border-2 ${active ? "bg-terracotta text-white border-terracotta" : d ? "bg-forest/10 text-forest border-forest/30" : "bg-white border-cream-alt"}`}>
                      Participant {i + 1}{d ? `: ${d.title || "design"}` : ""}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="theme-design-grid">
                {availableDesigns.map((d) => {
                  const selected = form.participant_designs[form.active_participant]?.design_id === d.id;
                  const count = form.participant_designs.slice(0, guestsN).filter((p) => p?.design_id === d.id).length;
                  return (
                    <button key={d.id} type="button" onClick={() => setParticipantDesign(form.active_participant, d.id)} data-testid={`theme-design-${d.id}`}
                      className={`text-left rounded-3xl overflow-hidden border-2 transition-all ${selected ? "border-terracotta ring-4 ring-terracotta/20 scale-[1.02]" : "border-cream-alt hover:border-terracotta/50"}`}>
                      <div className="relative aspect-square bg-cream-alt">
                        {d.image_url ? (
                          <img src={d.image_url} alt={d.title || "Design"} className="w-full h-full object-cover"/>
                        ) : (
                          <div className="w-full h-full grid place-items-center text-charcoal-muted"><ImageIcon size={32}/></div>
                        )}
                        {selected && <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-terracotta text-white grid place-items-center shadow-lg"><Check size={16}/></div>}
                        {count > 0 && <div className="absolute bottom-2 left-2 rounded-full bg-charcoal text-cream text-xs font-bold px-2.5 py-1" data-testid={`design-count-${d.id}`}>× {count}</div>}
                      </div>
                      <div className="p-3">
                        <div className="font-serif text-lg leading-tight">{d.title || "Untitled design"}</div>
                        {d.description && <div className="text-xs text-charcoal-muted mt-1 line-clamp-2">{d.description}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
              {!designsComplete && <div className="mt-4 text-charcoal-muted text-sm" data-testid="design-pick-hint">Choosing for <b>Participant {form.active_participant + 1}</b> — {guestsN - form.participant_designs.slice(0, guestsN).filter((p) => p?.design_id).length} left to assign.</div>}
            </div>
          )}
          {currentStepId === "design" && (
            <div>
              <h2 className="font-serif text-3xl">Design information</h2>
              {w.is_theme && hasDesignPicker && (
                <div className="mt-4 rounded-2xl bg-cream-alt p-4" data-testid="design-picks-summary">
                  <div className="text-xs text-charcoal-muted uppercase tracking-widest font-bold">Your picks</div>
                  <ul className="mt-1 text-sm">{form.participant_designs.slice(0, guestsN).map((p, i) => { const d = availableDesigns.find((x) => x.id === p?.design_id); return <li key={i}>Participant {i + 1}: <b>{d?.title || "design"}</b></li>; })}</ul>
                </div>
              )}
              {w.is_theme && <div className="mt-3 rounded-2xl bg-ochre/15 text-charcoal p-4 text-sm"><b>This is a themed session</b> — the design is pre-drawn and traced by our team. You can add notes below if you'd like.</div>}
              {designInstructions && (
                <div className="mt-4 rounded-2xl bg-cream-alt p-5 whitespace-pre-line text-sm text-charcoal/85" data-testid="design-instructions">{designInstructions}</div>
              )}
              {!hasDesignPicker && (
                <div className="mt-6" data-testid="design-upload-section">
                  <label className="text-sm font-semibold">Upload your own image (HEIC, JPG, PNG · max 4MB · up to {guestsN} — one per participant)</label>
                  {form.design_images.length < guestsN && (
                    <label className="mt-2 flex items-center gap-3 rounded-2xl border-2 border-dashed border-cream-alt px-4 py-5 cursor-pointer hover:border-terracotta" data-testid="design-upload-btn">
                      <Upload size={20} className="text-terracotta"/>
                      <span className="text-sm font-semibold text-terracotta">Upload your own image</span>
                      <input data-testid="design-file" type="file" accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif,.jpg,.jpeg,.png" className="hidden" onChange={onFile}/>
                    </label>
                  )}
                  {form.design_images.length > 0 && (
                    <div className="mt-4 grid gap-2" data-testid="participant-image-assign">
                      {Array.from({ length: guestsN }, (_, i) => (
                        <div key={i} className="flex items-center gap-3 rounded-2xl bg-cream-alt px-4 py-2.5 text-sm">
                          <span className="font-semibold w-28 shrink-0">Participant {i + 1}</span>
                          <select value={form.participant_designs[i]?.image_index ?? ""} onChange={(e) => assignImage(i, e.target.value === "" ? null : Number(e.target.value))} data-testid={`participant-image-${i}`} className="flex-1 rounded-xl border border-cream-alt px-3 py-2 bg-white">
                            <option value="">No image (describe below)</option>
                            {form.design_images.map((img, k) => <option key={k} value={k}>{img.name}</option>)}
                          </select>
                          {form.participant_designs[i]?.image_index != null && <img src={form.design_images[form.participant_designs[i].image_index]?.data} alt="" className="w-10 h-10 rounded-lg object-cover"/>}
                        </div>
                      ))}
                      <button type="button" onClick={() => setForm({ ...form, design_images: [], participant_designs: [], design_image_data: null, design_image_name: null })} data-testid="design-clear-images" className="justify-self-start text-xs text-red-600 underline">Remove uploaded images</button>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-6">
                <label className="text-sm font-semibold">Design notes or description</label>
                <textarea data-testid="design-notes" rows={4} value={form.design_notes} onChange={(e) => setForm({...form, design_notes: e.target.value})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
              </div>
            </div>
          )}
          {currentStepId === "info" && (
            <div>
              <h2 className="font-serif text-3xl">Your information</h2>
              <div className="mt-6 grid gap-4">
                <Field label="Full Name *" v={form.name} on={(v) => setForm({...form, name: v})} req name="name"/>
                <Field label="Email *" v={form.email} on={(v) => setForm({...form, email: v})} type="email" req name="email"/>
                <Field label="Phone" v={form.phone} on={(v) => setForm({...form, phone: v})} name="phone"/>
                <div>
                  <label className="text-sm font-semibold">Notes (optional)</label>
                  <textarea data-testid="booking-notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
                </div>
              </div>
            </div>
          )}
          {currentStepId === "review" && (
            <div>
              <h2 className="font-serif text-3xl">Review your booking</h2>
              <div className="mt-6 space-y-4 text-sm" data-testid="review-details">
                <Row k={w.is_theme ? "Theme" : "Workshop"} v={w.name}/>
                <Row k="Date & Time" v={`${fmtDateLong(w.date)} · ${fmtTime12(w.start_time)}`}/>
                <Row k="Guests" v={form.guests}/>
                {isRugFlow && <Row k="Rug sizes" v={breakdown.guestSizes.map((sz, i) => `Guest ${i + 1}: ${sz.label} ($${sz.sale_active && sz.sale_price_per_person != null ? sz.sale_price_per_person : sz.price_per_person})`).join(", ")}/>}
                {form.participant_designs.slice(0, guestsN).some((p) => p?.design_id) && <Row k="Designs" v={form.participant_designs.slice(0, guestsN).map((p, i) => `P${i + 1}: ${availableDesigns.find((x) => x.id === p?.design_id)?.title || "design"}`).join(", ")}/>}
                <Row k="Add-ons" v={breakdown.chosenAddons.length ? breakdown.chosenAddons.map((a) => a.id === "tuft_sip" ? "Sip" : a.label).join(", ") : "None"}/>
                {(breakdown.tax > 0 || breakdown.discount > 0) && <Row k="Subtotal" v={`$${breakdown.subtotal.toFixed(2)}`}/>}
                {breakdown.discount > 0 && <Row k={`Promo ${promo?.code}`} v={`-$${breakdown.discount.toFixed(2)}`}/>}
                {breakdown.tax > 0 && <Row k={`Sales tax (${breakdown.tax_rate}%)`} v={`$${breakdown.tax.toFixed(2)}`}/>}
                {breakdown.tip > 0 && <Row k="Tip" v={`$${breakdown.tip.toFixed(2)}`}/>}
                <Row k="Total" v={`$${breakdown.total.toFixed(2)}`}/>
                <Row k="Name" v={form.name}/>
                <Row k="Email" v={form.email}/>
                {form.design_images.length > 0 && <Row k="Images" v={form.participant_designs.slice(0, guestsN).map((p, i) => `P${i + 1}: ${p?.image_index != null ? form.design_images[p.image_index]?.name : "none"}`).join(", ")}/>}
              </div>
            </div>
          )}
          {currentStepId === "policies" && (
            <div>
              <h2 className="font-serif text-3xl">Policies & payment</h2>
              <p className="mt-2 text-charcoal/70">Please accept all required policies before continuing. Payment is 100% due at booking.</p>
              <div className="mt-5 flex gap-2" data-testid="promo-box">
                <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Promo code" data-testid="promo-input" className="flex-1 rounded-full border border-cream-alt px-4 py-2.5 text-sm uppercase"/>
                <button type="button" onClick={applyPromo} data-testid="promo-apply" className="rounded-full bg-charcoal text-cream px-5 py-2.5 text-sm font-semibold">Apply</button>
                {promo && <button type="button" onClick={() => { setPromo(null); setPromoInput(""); setForm((f) => ({ ...f, promo_code: "" })); }} data-testid="promo-remove" className="rounded-full bg-cream-alt px-4 py-2.5 text-sm">Remove</button>}
              </div>
              {promo && <div className="mt-2 text-xs text-forest font-semibold" data-testid="promo-applied">{promo.code} applied{promo.description ? ` — ${promo.description}` : ""}</div>}
              <div className="mt-4 rounded-2xl bg-white border border-cream-alt p-4" data-testid="tip-box">
                <div className="text-sm font-semibold">Add a tip for the studio team? <span className="text-charcoal-muted font-normal">(optional)</span></div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[[0, "No tip"], [10, "10%"], [15, "15%"], [20, "20%"], ["custom", "Custom"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setForm({ ...form, tip_choice: v })} data-testid={`tip-${v}`}
                      className={`rounded-full px-4 py-2 text-sm font-semibold border-2 ${form.tip_choice === v ? "bg-terracotta text-white border-terracotta" : "border-cream-alt hover:border-terracotta/50"}`}>{l}</button>
                  ))}
                  {form.tip_choice === "custom" && <input type="number" min="0" step="0.01" value={form.tip_custom} onChange={(e) => setForm({ ...form, tip_custom: e.target.value })} placeholder="$ amount" data-testid="tip-custom-input" className="w-28 rounded-full border border-cream-alt px-3 py-2 text-sm"/>}
                </div>
              </div>
              <div className="mt-4 rounded-2xl bg-cream-alt p-4 text-sm space-y-1.5" data-testid="prepay-summary">
                <div className="font-serif text-lg mb-1">You're about to pay for</div>
                <div className="flex justify-between gap-4"><span className="text-charcoal-muted">{w.is_theme ? "Theme" : "Workshop"}</span><b className="text-right">{w.name}</b></div>
                <div className="flex justify-between gap-4"><span className="text-charcoal-muted">Date & time</span><b className="text-right">{fmtDateLong(w.date)} · {fmtTime12(w.start_time)}</b></div>
                <div className="flex justify-between gap-4"><span className="text-charcoal-muted">Guests</span><b>{form.guests}</b></div>
                {isRugFlow && breakdown.guestSizes?.length > 0 && <div className="flex justify-between gap-4"><span className="text-charcoal-muted">Rug sizes</span><b className="text-right" data-testid="prepay-sizes">{breakdown.guestSizes.map((sz, i) => `G${i + 1}: ${sz.label}`).join(", ")}</b></div>}
                {form.participant_designs.slice(0, guestsN).some((p) => p?.design_id) && <div className="flex justify-between gap-4"><span className="text-charcoal-muted">Designs</span><b className="text-right">{form.participant_designs.slice(0, guestsN).map((p, i) => `P${i + 1}: ${availableDesigns.find((x) => x.id === p?.design_id)?.title || "design"}`).join(", ")}</b></div>}
                <div className="flex justify-between gap-4"><span className="text-charcoal-muted">Add-ons</span><b className="text-right">{breakdown.chosenAddons.length ? breakdown.chosenAddons.map((a) => a.id === "tuft_sip" ? "Sip" : a.label).join(", ") : "None"}</b></div>
                <div className="flex justify-between gap-4 border-t border-charcoal/10 pt-2 mt-2"><span className="text-charcoal-muted">Subtotal</span><b data-testid="prepay-subtotal">${breakdown.subtotal.toFixed(2)}</b></div>
                {breakdown.discount > 0 && <div className="flex justify-between gap-4 text-forest"><span>Promo {promo?.code}</span><b data-testid="prepay-discount">-${breakdown.discount.toFixed(2)}</b></div>}
                {breakdown.tax > 0 && <div className="flex justify-between gap-4"><span className="text-charcoal-muted">Sales tax ({breakdown.tax_rate}%)</span><b data-testid="prepay-tax">${breakdown.tax.toFixed(2)}</b></div>}
                {breakdown.tip > 0 && <div className="flex justify-between gap-4"><span className="text-charcoal-muted">Tip</span><b data-testid="prepay-tip">${breakdown.tip.toFixed(2)}</b></div>}
                <div className="flex justify-between gap-4 border-t border-charcoal/10 pt-2 mt-2 text-base"><span className="font-semibold">Total due now</span><b className="text-terracotta" data-testid="prepay-total">${breakdown.total.toFixed(2)}</b></div>
              </div>
              <div className="mt-8 rounded-2xl bg-terracotta/10 border-2 border-terracotta p-5" data-testid="policies-warning">
                <div className="font-display text-2xl md:text-3xl font-bold text-terracotta leading-tight">PLEASE READ ALL POLICIES BEFORE CHECKING THE BOX BELOW</div>
                <p className="mt-2 text-sm font-semibold text-charcoal">By checking the agreement box, you are confirming that you have read and understand the policies below. Please do not check the box without reviewing them.</p>
              </div>
              <div className="mt-4 space-y-3" data-testid="policies-list">
                {policies.map((p) => (
                  <div key={p.id} className="rounded-2xl p-4 border-2 border-cream-alt bg-white" data-testid={`policy-${p.id}`}>
                    <div className="font-bold">{p.title}</div>
                    <div className="text-sm text-charcoal/75 mt-1">{p.body}</div>
                  </div>
                ))}
                <label className={`flex items-start gap-3 rounded-2xl p-4 border-2 cursor-pointer ${allAccepted ? "border-forest bg-forest/5" : "border-terracotta/50 bg-terracotta/5"}`}>
                  <input data-testid="policy-agree-all" type="checkbox" checked={allAccepted} onChange={(e) => toggleAll(e.target.checked)} className="mt-1 w-5 h-5 accent-forest"/>
                  <div className="font-bold">I have read, understand, and agree to That's Tuft Creative Co.'s payment/refund, cancellation, rescheduling, late arrival, and design policies. <span className="text-terracotta">*</span></div>
                </label>
                {tuftSipSelected && (
                  <label className={`flex items-start gap-3 rounded-2xl p-4 border-2 cursor-pointer ${form.tuft_sip_acknowledged ? "border-forest bg-forest/5" : "border-terracotta/40 bg-terracotta/5"}`}>
                    <input data-testid="tuft-sip-ack" type="checkbox" checked={form.tuft_sip_acknowledged} onChange={(e) => setForm({...form, tuft_sip_acknowledged: e.target.checked})} className="mt-1 w-4 h-4 accent-forest"/>
                    <div>
                      <div className="font-bold">I have read and agree to the Sip terms.<span className="text-terracotta ml-1">*</span></div>
                      <div className="text-xs text-charcoal-muted mt-1 whitespace-pre-line">{tuftSipDisclaimer}</div>
                    </div>
                  </label>
                )}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-charcoal text-cream rounded-3xl p-6 shadow-hover" data-testid="booking-summary">
            <div className="text-ochre-light text-xs tracking-widest uppercase font-bold">Your booking</div>
            <h3 className="mt-2 font-serif text-2xl leading-tight">{w.name}</h3>
            <div className="mt-1 text-xs text-cream/70">{fmtDateLong(w.date, { month: 'short', day: 'numeric' })} · {fmtTime12(w.start_time)}</div>
            {selectedDesign && (
              <div className="mt-4 flex items-center gap-2 text-xs text-cream/80">
                {selectedDesign.image_url && <img src={selectedDesign.image_url} alt="" className="w-10 h-10 rounded-lg object-cover"/>}
                <span>{selectedDesign.title || "Design selected"}</span>
              </div>
            )}
            <div className="mt-6 space-y-2 text-sm">
              <SumRow
                label={isRugFlow ? (breakdown.guestSizes?.length ? (new Set(breakdown.guestSizes.map((s) => s.id)).size > 1 ? `Mixed sizes × ${breakdown.guests}` : `${breakdown.guestSizes[0].label} × ${breakdown.guests}`) : "Pick sizes") : `${w.name} × ${breakdown.guests}`}
                value={`$${breakdown.rug_subtotal.toFixed(2)}`}/>
              {breakdown.chosenAddons.map((a) => (
                <SumRow key={a.id} label={`${a.id === "tuft_sip" ? "Sip" : a.label} × ${breakdown.guests}`} value={`$${(a.price_per_person * breakdown.guests).toFixed(2)}`}/>
              ))}
              {(breakdown.tax > 0 || breakdown.discount > 0) && <SumRow label="Subtotal" value={`$${breakdown.subtotal.toFixed(2)}`}/>}
              {breakdown.discount > 0 && <SumRow label={`Promo ${promo?.code}`} value={`-$${breakdown.discount.toFixed(2)}`}/>}
              {breakdown.tax > 0 && <SumRow label={`Sales tax (${breakdown.tax_rate}%)`} value={`$${breakdown.tax.toFixed(2)}`}/>}
              {breakdown.tip > 0 && <SumRow label="Tip" value={`$${breakdown.tip.toFixed(2)}`}/>}
            </div>
            <div className="mt-6 pt-6 border-t border-white/15 flex items-center justify-between">
              <span className="font-serif text-lg">Total</span>
              <span className="font-serif text-2xl text-ochre-light">${breakdown.total.toFixed(2)}</span>
            </div>
            <p className="mt-3 text-xs text-cream/60">100% due at checkout · secure via Stripe</p>
          </div>
        </aside>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={prev} disabled={step === 0} data-testid="wizard-prev" className="rounded-full bg-cream-alt text-charcoal px-6 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-40"><ChevronLeft size={16}/> Back</button>
        {step < STEPS.length - 1 ? (
          <button onClick={next} disabled={!canNext()} data-testid="wizard-next" className="rounded-full bg-terracotta text-white px-8 py-3 font-bold inline-flex items-center gap-2 shadow-button disabled:opacity-40">Continue <ChevronRight size={16}/></button>
        ) : (
          <button onClick={submit} disabled={missing.length > 0 || (tuftSipSelected && !form.tuft_sip_acknowledged) || busy} data-testid="wizard-pay" className="rounded-full bg-terracotta text-white px-8 py-3 font-bold inline-flex items-center gap-2 shadow-button disabled:opacity-40"><CreditCard size={16}/> {busy ? "Redirecting to Stripe…" : `Pay $${breakdown.total.toFixed(2)}`}</button>
        )}
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", req, name }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input data-testid={`booking-${name}`} required={req} type={type} value={v} onChange={(e) => on(e.target.value)}
        className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-cream-alt pb-3">
      <span className="text-charcoal-muted font-medium">{k}</span>
      <span className="font-semibold text-right">{v}</span>
    </div>
  );
}
function SumRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-cream/80">{label}</span>
      <span>{value}</span>
    </div>
  );
}
